import React, { useState, useEffect } from 'react';
import { ShieldCheck, Cookie, X, Sliders } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { updateConsent } from '../../utils/analytics';

const CONSENT_STORAGE_KEY = 'we24_cookie_consent_v2';

export interface ConsentPreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  decided: boolean;
}

const getStoredConsent = (): ConsentPreferences | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const CookieConsent: React.FC = () => {
  const { language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [analyticsAllowed, setAnalyticsAllowed] = useState(false);
  const [marketingAllowed, setMarketingAllowed] = useState(false);

  useEffect(() => {
    const stored = getStoredConsent();
    if (!stored || !stored.decided) {
      // Delay showing banner slightly to prioritize hero LCP & initial render
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1200);
      return () => clearTimeout(timer);
    } else {
      updateConsent(stored.analytics, stored.marketing);
    }
  }, []);

  // Listen for open cookie settings trigger (e.g., from Footer)
  useEffect(() => {
    const handleOpenSettings = () => {
      const stored = getStoredConsent();
      if (stored) {
        setAnalyticsAllowed(stored.analytics);
        setMarketingAllowed(stored.marketing);
      }
      setShowPreferences(true);
      setIsVisible(true);
    };

    window.addEventListener('open_cookie_preferences', handleOpenSettings);
    return () => window.removeEventListener('open_cookie_preferences', handleOpenSettings);
  }, []);

  const savePreferences = (analytics: boolean, marketing: boolean) => {
    const preferences: ConsentPreferences = {
      necessary: true,
      analytics,
      marketing,
      decided: true
    };
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(preferences));
    } catch {}

    updateConsent(analytics, marketing);
    setIsVisible(false);
    setShowPreferences(false);
  };

  const handleAcceptAll = () => {
    savePreferences(true, true);
  };

  const handleRejectAll = () => {
    savePreferences(false, false);
  };

  const handleSaveCustom = () => {
    savePreferences(analyticsAllowed, marketingAllowed);
  };

  if (!isVisible) return null;

  const isTR = language === 'TR';

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label={isTR ? "Çerez ve Gizlilik Tercihleri" : "Cookie and Privacy Preferences"}
      className="fixed bottom-4 left-4 right-4 sm:left-6 sm:right-auto sm:max-w-md z-[9999] bg-[#071927]/95 border border-white/15 backdrop-blur-xl rounded-2xl p-5 sm:p-6 shadow-2xl text-zinc-100 animate-in fade-in slide-in-from-bottom-5 duration-300"
    >
      {!showPreferences ? (
        <div>
          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-xl bg-[#ff7a63]/15 text-[#ff7a63] shrink-0 border border-[#ff7a63]/30">
              <Cookie className="w-5 h-5" aria-hidden="true" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white tracking-tight">
                {isTR ? "Gizlilik ve Çerez Tercihleri" : "Privacy & Cookie Preferences"}
              </h2>
              <p className="text-xs text-zinc-300 leading-relaxed mt-1.5">
                {isTR
                  ? "Deneyiminizi optimize etmek, site performansını anonim ölçümlemek ve size en uygun içerikleri sunmak amacıyla çerezler kullanıyoruz."
                  : "We use cookies to optimize your browsing experience, measure site performance anonymously, and provide relevant content."}
                {" "}
                <a
                  href={isTR ? "/cerez-politikasi" : "/en/cookie-policy"}
                  className="text-[#ff7a63] underline hover:text-[#ff927f] font-medium"
                >
                  {isTR ? "Çerez Politikası" : "Cookie Policy"}
                </a>
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 mt-5">
            <button
              type="button"
              onClick={handleAcceptAll}
              className="flex-1 py-2.5 px-4 bg-white text-zinc-950 hover:bg-zinc-200 text-xs font-bold rounded-xl transition-all shadow-md focus-visible:ring-2 focus-visible:ring-[#ff7a63]"
            >
              {isTR ? "Tümünü Kabul Et" : "Accept All"}
            </button>
            <button
              type="button"
              onClick={handleRejectAll}
              className="flex-1 py-2.5 px-4 bg-white/10 hover:bg-white/20 text-white border border-white/10 text-xs font-bold rounded-xl transition-all focus-visible:ring-2 focus-visible:ring-[#ff7a63]"
            >
              {isTR ? "Reddet" : "Reject"}
            </button>
            <button
              type="button"
              onClick={() => setShowPreferences(true)}
              className="py-2.5 px-3 bg-transparent hover:bg-white/5 text-zinc-400 hover:text-white text-xs font-medium rounded-xl transition-all border border-transparent hover:border-white/10 flex items-center justify-center gap-1.5"
              aria-label={isTR ? "Tercihleri Yönet" : "Manage Preferences"}
            >
              <Sliders className="w-3.5 h-3.5" aria-hidden="true" />
              <span className="sm:hidden">{isTR ? "Tercihler" : "Preferences"}</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between border-b border-white/10 pb-3">
            <h2 className="text-sm font-bold text-white flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-[#ff7a63]" aria-hidden="true" />
              {isTR ? "Çerez Tercihlerini Yönet" : "Manage Cookie Preferences"}
            </h2>
            <button
              type="button"
              onClick={() => setShowPreferences(false)}
              className="text-zinc-400 hover:text-white p-1 rounded-lg hover:bg-white/10"
              aria-label={isTR ? "Kapat" : "Close"}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-3 text-xs">
            {/* Necessary */}
            <div className="flex items-center justify-between p-2.5 rounded-lg bg-white/5 border border-white/5">
              <div>
                <span className="font-bold text-white block">
                  {isTR ? "Zorunlu Çerezler" : "Necessary Cookies"}
                </span>
                <span className="text-zinc-400 text-[11px]">
                  {isTR ? "Web sitesinin temel güvenliği ve çalışması için gereklidir." : "Required for core security and navigation."}
                </span>
              </div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-400 bg-emerald-950/60 px-2 py-0.5 rounded border border-emerald-500/30">
                {isTR ? "Aktif" : "Active"}
              </span>
            </div>

            {/* Analytics */}
            <label className="flex items-center justify-between p-2.5 rounded-lg bg-white/5 border border-white/5 cursor-pointer hover:bg-white/[0.08] transition-colors">
              <div>
                <span className="font-bold text-white block">
                  {isTR ? "Performans & Analitik" : "Performance & Analytics"}
                </span>
                <span className="text-zinc-400 text-[11px]">
                  {isTR ? "Sayfa ziyaretlerini ve dönüşümleri anonim olarak ölçümler." : "Measures page visits and conversions anonymously."}
                </span>
              </div>
              <input
                type="checkbox"
                checked={analyticsAllowed}
                onChange={(e) => setAnalyticsAllowed(e.target.checked)}
                className="w-4 h-4 rounded border-zinc-700 bg-zinc-900 text-[#ff7a63] focus:ring-[#ff7a63]"
              />
            </label>

            {/* Marketing */}
            <label className="flex items-center justify-between p-2.5 rounded-lg bg-white/5 border border-white/5 cursor-pointer hover:bg-white/[0.08] transition-colors">
              <div>
                <span className="font-bold text-white block">
                  {isTR ? "Pazarlama & Kişiselleştirme" : "Marketing & Personalization"}
                </span>
                <span className="text-zinc-400 text-[11px]">
                  {isTR ? "İçeriklerin ilgi alanlarınıza göre sunulmasını sağlar." : "Tailors content according to relevant interests."}
                </span>
              </div>
              <input
                type="checkbox"
                checked={marketingAllowed}
                onChange={(e) => setMarketingAllowed(e.target.checked)}
                className="w-4 h-4 rounded border-zinc-700 bg-zinc-900 text-[#ff7a63] focus:ring-[#ff7a63]"
              />
            </label>
          </div>

          <div className="flex gap-2 pt-2 border-t border-white/10">
            <button
              type="button"
              onClick={handleSaveCustom}
              className="flex-1 py-2 px-3 bg-white text-zinc-950 hover:bg-zinc-200 text-xs font-bold rounded-xl transition-all focus-visible:ring-2 focus-visible:ring-[#ff7a63]"
            >
              {isTR ? "Seçimleri Kaydet" : "Save Preferences"}
            </button>
            <button
              type="button"
              onClick={handleAcceptAll}
              className="py-2 px-3 bg-white/10 hover:bg-white/20 text-white text-xs font-bold rounded-xl border border-white/10 transition-all"
            >
              {isTR ? "Tümünü Onayla" : "Accept All"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CookieConsent;
